import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRightIcon, XIcon, PlusIcon } from 'lucide-react';
interface PersonaTemplateProps {
  sectionId: string;
  featureOptions?: {
    id: string;
    name: string;
  }[];
  selectedFeatureIds?: string[];
  onFeatureSelectionChange?: (ids: string[]) => void;
  onAddFeature?: (title: string, fields: Record<string, string>) => void;
}
interface TraitSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}
function TraitSlider({
  label,
  value,
  onChange
}: TraitSliderProps) {
  return <div className="mb-6" data-id="element-297">
      <div className="flex items-center justify-between mb-2" data-id="element-298">
        <span className="text-sm font-medium text-stone-800" data-id="element-299">{label}</span>
        <span className="text-xs text-stone-400" data-id="element-300">
          Mean: {value.toFixed(1)}
          <br data-id="element-301" />
          Std Dev: 15.0
        </span>
      </div>
      <input type="range" min={0} max={100} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-1.5 bg-stone-200 rounded-full appearance-none cursor-pointer accent-stone-800 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-stone-800 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-stone-800 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-302" />
      <div className="flex justify-between mt-1" data-id="element-303">
        <span className="text-xs text-stone-400" data-id="element-304">20 - 80</span>
        <span className="text-xs text-stone-400" data-id="element-305">Δ 60</span>
      </div>
    </div>;
}
const TRAIT_LABELS = ['Sensitivity to Time', 'Cognitive Load', 'Persistence', 'Curiosity', 'Tech Proficiency', 'Intuition'];
const RADAR_LABELS = ['Time Sensitivity', 'Cognitive Load', 'Persistence', 'Curiosity', 'Tech Proficiency', 'Intuition'];
function RadarChartSVG({
  data
}: {
  data: {
    label: string;
    value: number;
  }[];
}) {
  const cx = 150;
  const cy = 125;
  const maxR = 80;
  const levels = 4;
  const n = data.length;
  const angleStep = 2 * Math.PI / n;
  const startAngle = -Math.PI / 2;
  const getPoint = (index: number, radius: number) => {
    const angle = startAngle + index * angleStep;
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle)
    };
  };
  // Grid circles
  const gridPaths = Array.from({
    length: levels
  }, (_, level) => {
    const r = maxR * (level + 1) / levels;
    const points = Array.from({
      length: n
    }, (_, i) => {
      const p = getPoint(i, r);
      return `${p.x},${p.y}`;
    }).join(' ');
    return <polygon key={level} points={points} fill="none" stroke="#d6d3d1" strokeWidth={0.75} data-id="element-306" />;
  });
  // Axis lines
  const axisLines = Array.from({
    length: n
  }, (_, i) => {
    const p = getPoint(i, maxR);
    return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#d6d3d1" strokeWidth={0.75} data-id="element-307" />;
  });
  // Data polygon
  const dataPoints = data.map((d, i) => {
    const r = d.value / 100 * maxR;
    const p = getPoint(i, r);
    return `${p.x},${p.y}`;
  }).join(' ');
  // Labels
  const labels = data.map((d, i) => {
    const labelR = maxR + 18;
    const p = getPoint(i, labelR);
    const angle = startAngle + i * angleStep;
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (Math.cos(angle) > 0.3) textAnchor = 'start';else if (Math.cos(angle) < -0.3) textAnchor = 'end';
    return <text key={i} x={p.x} y={p.y} textAnchor={textAnchor} dominantBaseline="central" fontSize={9} fill="#78716c" data-id="element-308">
        {d.label}
      </text>;
  });
  return <svg viewBox="0 0 300 250" className="w-full h-full" overflow="visible" data-id="element-309">
      {gridPaths}
      {axisLines}
      <polygon points={dataPoints} fill="#78716c" fillOpacity={0.3} stroke="#44403c" strokeWidth={1.5} data-id="element-310" />
      {labels}
    </svg>;
}
const OCCUPATION_SUGGESTIONS = ['Software Engineer', 'Product Manager', 'Designer', 'Marketing Specialist', 'Sales Professional', 'Teacher', 'Healthcare Worker', 'Entrepreneur', 'Student', 'Consultant'];
const ADD_FEATURE_FIELDS = [{
  id: 'description',
  label: 'Description',
  placeholder: 'Describe what this feature does and the problem it solves…'
}, {
  id: 'strategic-goals',
  label: 'Strategic Goals',
  placeholder: 'What business or product goals does this feature support?'
}, {
  id: 'expected-usage',
  label: 'Expected Usage',
  placeholder: 'How and how often will users interact with this feature?'
}];
export function PersonaTemplate({
  sectionId,
  featureOptions = [],
  selectedFeatureIds = [],
  onFeatureSelectionChange,
  onAddFeature
}: PersonaTemplateProps) {
  const [traits, setTraits] = useState<Record<string, number>>({
    'Sensitivity to Time': 50,
    'Cognitive Load': 50,
    Persistence: 50,
    Curiosity: 50,
    'Tech Proficiency': 50,
    Intuition: 50
  });
  const [occupation, setOccupation] = useState('');
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [newFeatureTitle, setNewFeatureTitle] = useState('');
  const [newFeatureFields, setNewFeatureFields] = useState<Record<string, string>>({});
  const radarData = TRAIT_LABELS.map((label, i) => ({
    label: RADAR_LABELS[i],
    value: traits[label]
  }));
  const updateTrait = (label: string, value: number) => {
    setTraits(prev => ({
      ...prev,
      [label]: value
    }));
  };
  const toggleFeature = (featureId: string) => {
    if (!onFeatureSelectionChange) return;
    if (selectedFeatureIds.includes(featureId)) {
      onFeatureSelectionChange(selectedFeatureIds.filter(id => id !== featureId));
    } else {
      onFeatureSelectionChange([...selectedFeatureIds, featureId]);
    }
  };
  const handleSaveFeature = () => {
    const title = newFeatureTitle.trim();
    if (!title || !onAddFeature) return;
    onAddFeature(title, newFeatureFields);
    setNewFeatureTitle('');
    setNewFeatureFields({});
    setShowAddFeatureModal(false);
  };
  const handleCloseModal = () => {
    setShowAddFeatureModal(false);
    setNewFeatureTitle('');
    setNewFeatureFields({});
  };
  return <div className="mt-6 space-y-8" data-id="element-311">
      {/* Occupation Section — moved to top */}
      <div className="space-y-6" data-id="element-312">
        <div className="grid grid-cols-[180px_1fr] gap-4 items-start" data-id="element-313">
          <label className="text-sm font-medium text-stone-800 pt-2" data-id="element-314">
            Occupation?
          </label>
          <div data-id="element-315">
            <input type="text" value={occupation} onChange={e => setOccupation(e.target.value)} placeholder="Enter occupation or select from suggestions below" className="w-full px-3 py-2 text-sm border-b border-stone-200 bg-transparent outline-none focus:border-stone-400 transition-colors placeholder:text-stone-400 mb-3" data-id="element-316" />
            <p className="text-xs text-stone-400 mb-2" data-id="element-317">Suggestions:</p>
            <div className="flex flex-wrap gap-2" data-id="element-318">
              {OCCUPATION_SUGGESTIONS.map(occ => <button key={occ} type="button" onClick={() => setOccupation(occ)} className={`px-3 py-1.5 text-sm rounded-none border transition-colors duration-100 ${occupation === occ ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-319">
                  {occ}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Personality Traits Section */}
      <div className="border-t border-stone-200 pt-8" data-id="element-320">
        <p className="text-sm text-stone-500 mb-6" data-id="element-321">
          Tune each trait range. We derive the mean and variance automatically
          and update the radar preview in real time.
        </p>

        <div className="flex gap-8" data-id="element-322">
          {/* Sliders Column */}
          <div className="flex-1 min-w-0" data-id="element-323">
            {TRAIT_LABELS.map(label => <TraitSlider key={label} label={label} value={traits[label]} onChange={v => updateTrait(label, v)} data-id="element-324" />)}
          </div>

          {/* Radar Chart Column */}
          <div className="w-72 flex-shrink-0" data-id="element-325">
            <div className="border border-stone-200 rounded-none p-5 bg-stone-50/50" data-id="element-326">
              <h4 className="text-sm font-semibold text-stone-700 mb-3" data-id="element-327">
                Live Radar Preview
              </h4>
              <div className="h-60" data-id="element-328">
                <RadarChartSVG data={radarData} data-id="element-329" />
              </div>
              <p className="text-xs text-stone-400 text-center mt-3" data-id="element-330">
                Hover over the radar to preview the bell curve for a trait.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Targeted Features */}
      <div className="border-t border-stone-200 pt-8" data-id="element-331">
        <div className="grid grid-cols-[180px_1fr] gap-4 items-start" data-id="element-332">
          <label className="text-sm font-medium text-stone-800 pt-2" data-id="element-333">
            Which features are targeted at this persona?
          </label>
          <div data-id="element-334">
            {featureOptions.length > 0 ? <>
                <div className="flex flex-wrap gap-2" data-id="element-335">
                  {featureOptions.map(feature => {
                const isSelected = selectedFeatureIds.includes(feature.id);
                return <button key={feature.id} type="button" onClick={() => toggleFeature(feature.id)} className={`px-3 py-1.5 text-sm rounded-none border transition-colors duration-100 ${isSelected ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-336">
                        {feature.name}
                      </button>;
              })}
                </div>
                {onAddFeature && <button type="button" onClick={() => setShowAddFeatureModal(true)} className="inline-flex items-center gap-1 mt-3 text-xs text-stone-400 hover:text-stone-600 transition-colors duration-100" data-id="element-337">
                    <PlusIcon className="w-3 h-3" data-id="element-338" />
                    <span data-id="element-339">Add a Feature</span>
                  </button>}
              </> : <div className="flex items-center justify-between pt-2" data-id="element-340">
                <p className="text-sm text-stone-400 italic" data-id="element-341">
                  No features defined yet.
                </p>
                {onAddFeature && <button type="button" onClick={() => setShowAddFeatureModal(true)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors duration-100 flex-shrink-0" data-id="element-342">
                    <PlusIcon className="w-3.5 h-3.5" data-id="element-343" />
                    <span data-id="element-344">Add a Feature</span>
                  </button>}
              </div>}
          </div>
        </div>
      </div>

      {/* Add Feature Modal */}
      {showAddFeatureModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center" data-id="element-345">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={handleCloseModal} data-id="element-346" />
            <div className="relative bg-white rounded-none shadow-2xl border border-stone-200 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto z-10" data-id="element-347">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200" data-id="element-348">
                <h2 className="text-lg font-semibold text-stone-900" data-id="element-349">
                  New Feature
                </h2>
                <button type="button" onClick={handleCloseModal} className="w-7 h-7 flex items-center justify-center rounded-none text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors duration-100" aria-label="Close" data-id="element-350">
                  <XIcon className="w-4.5 h-4.5" data-id="element-351" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5" data-id="element-352">
                {/* Feature Name */}
                <div data-id="element-353">
                  <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-354">
                    Feature Name
                  </label>
                  <input type="text" value={newFeatureTitle} onChange={e => setNewFeatureTitle(e.target.value)} placeholder="Enter feature name…" className="w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white transition-colors duration-100 placeholder:text-stone-400" autoFocus data-id="element-355" />
                </div>

                {/* Feature Fields */}
                {ADD_FEATURE_FIELDS.map(field => <div key={field.id} data-id="element-356">
                    <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-357">
                      {field.label}
                    </label>
                    <textarea value={newFeatureFields[field.id] ?? ''} onChange={e => setNewFeatureFields(prev => ({
              ...prev,
              [field.id]: e.target.value
            }))} placeholder={field.placeholder} rows={3} className="w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 min-h-[3em] focus:border-stone-300 focus:bg-white transition-colors duration-100 placeholder:text-stone-400 resize-none" data-id="element-358" />
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-2" data-id="element-359">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-stone-600 rounded-none border border-stone-200 hover:bg-stone-50 transition-colors duration-100" data-id="element-360">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveFeature} disabled={!newFeatureTitle.trim()} className="px-4 py-2 text-sm font-medium text-white bg-stone-800 rounded-none hover:bg-stone-700 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-361">
                  Save Feature
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}